import { useEffect, useState } from 'react'
import type { ComponentType } from 'react'
import { useTranslation } from 'react-i18next'
import { useRecoilValue } from 'recoil'
import { z } from 'zod'
import type { ProofShareableProduct } from '@messenger/types'
import {
  MAX_PROOF_SHARE_COMMENT_LENGTH,
  MAX_PROOF_SHARE_RECIPIENTS,
} from '@messenger/types'
import { userAtom } from '@/atoms/_globalAtoms'
import { applicationTypeAtom } from '@/atoms/globalAtoms'
import { createProofShare, getShareableProofProducts } from '@/api/proofShare'
import { useSetToast } from '@/hooks'
import { FullscreenModal } from '@/components/global/FullscreenModal/FullscreenModal'
import { Tooltip } from '@/components/global/Tooltip/Tooltip'
import Flex from '@/components/priority/Flex/Flex'
import Icon from '@/components/priority/Icon/Icon'
import Text from '@/components/priority/Text/Text'
import {
  Button,
  Input as RawInput,
  TextArea as RawTextArea,
} from '@/components/priority'
import * as S from './ShareProofModal.styled'

// The shared Input/TextArea are untyped JS forwardRef components (tuple `value`
// prop); loosen them for TSX use — same shim as RecipeBuilder.tsx.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LooseProps = Record<string, any>
const Input = RawInput as unknown as ComponentType<LooseProps>
const TextArea = RawTextArea as unknown as ComponentType<LooseProps>

const emailSchema = z.string().trim().pipe(z.email())

const isSameEmail = (a: string, b: string): boolean =>
  a.toLowerCase() === b.toLowerCase()

type ShareProofModalProps = {
  show: boolean
  orderId?: string
  onClose: () => void
  // The product the user opened the modal from is auto-selected once the
  // product list loads (mirrors the M3 modal's preselection).
  preselectedOrderProductId?: string
}

export const ShareProofModal = ({
  show,
  orderId,
  onClose,
  preselectedOrderProductId,
}: ShareProofModalProps) => {
  const { t } = useTranslation()
  const basePath = 'proofShare.modal'
  const setToast = useSetToast()

  const user = useRecoilValue(userAtom)
  const applicationType = useRecoilValue(applicationTypeAtom) as 'MDL' | 'M3'

  const [replyTo, setReplyTo] = useState('')
  const [replyToError, setReplyToError] = useState<string | null>(null)
  const [recipients, setRecipients] = useState<string[]>([])
  const [emailInput, setEmailInput] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [comment, setComment] = useState('')
  const [products, setProducts] = useState<ProofShareableProduct[]>([])
  const [isLoadingProducts, setIsLoadingProducts] = useState(false)
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(
    new Set(),
  )
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Reset the form each time the modal opens and load the shareable products.
  useEffect(() => {
    if (!show) return

    setReplyTo(user?.email ?? '')
    setReplyToError(null)
    setRecipients([])
    setEmailInput('')
    setEmailError(null)
    setComment('')
    setSelectedProductIds(new Set())
    setProducts([])

    if (!orderId) return

    let isActive = true
    setIsLoadingProducts(true)
    getShareableProofProducts(orderId, applicationType)
      .then((response) => {
        if (!isActive) return
        const loadedProducts = response.data.products ?? []
        setProducts(loadedProducts)
        // Auto-select the product the modal was opened from, if shareable.
        const preselected = loadedProducts.find(
          (product) =>
            product.orderProductId === preselectedOrderProductId &&
            (product.ready || product.canGenerate),
        )
        if (preselected) {
          setSelectedProductIds(new Set([preselected.orderProductId]))
        }
      })
      .catch((error) => {
        console.error('Failed to load shareable products:', error)
        if (isActive) setProducts([])
      })
      .finally(() => {
        if (isActive) setIsLoadingProducts(false)
      })

    return () => {
      isActive = false
    }
    // user?.email intentionally excluded: it only seeds the reply-to field on
    // open so a user-typed value is never clobbered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, orderId, applicationType, preselectedOrderProductId])

  const addEmail = (raw: string): boolean => {
    const value = raw.trim()
    if (!value) return false

    const result = emailSchema.safeParse(value)
    if (!result.success) {
      setEmailError(t(`${basePath}.validation.invalidEmail`))
      return false
    }

    // Duplicates are silently ignored (not an error) — just clear the input.
    if (recipients.some((email) => isSameEmail(email, result.data))) {
      setEmailInput('')
      setEmailError(null)
      return true
    }

    if (recipients.length >= MAX_PROOF_SHARE_RECIPIENTS) {
      setEmailError(
        t(`${basePath}.validation.maxRecipients`, {
          max: MAX_PROOF_SHARE_RECIPIENTS,
        }),
      )
      return false
    }

    setRecipients((prev) => [...prev, result.data])
    setEmailInput('')
    setEmailError(null)
    return true
  }

  const removeEmail = (email: string) => {
    setRecipients((prev) => prev.filter((existing) => existing !== email))
  }

  const toggleProduct = (orderProductId: string) => {
    setSelectedProductIds((prev) => {
      const next = new Set(prev)
      if (next.has(orderProductId)) {
        next.delete(orderProductId)
      } else {
        next.add(orderProductId)
      }
      return next
    })
  }

  const handleSubmit = async () => {
    if (!orderId || isSubmitting) return

    // Flush a typed-but-not-yet-added email so a user who clicks Share without
    // pressing Enter still includes it.
    const pending = emailInput.trim()
    let finalRecipients = recipients
    if (pending) {
      const result = emailSchema.safeParse(pending)
      if (!result.success) {
        setEmailError(t(`${basePath}.validation.invalidEmail`))
        return
      }
      if (!recipients.some((email) => isSameEmail(email, result.data))) {
        if (recipients.length >= MAX_PROOF_SHARE_RECIPIENTS) {
          setEmailError(
            t(`${basePath}.validation.maxRecipients`, {
              max: MAX_PROOF_SHARE_RECIPIENTS,
            }),
          )
          return
        }
        finalRecipients = [...recipients, result.data]
        setRecipients(finalRecipients)
      }
      setEmailInput('')
      setEmailError(null)
    }

    if (finalRecipients.length === 0) {
      setEmailError(t(`${basePath}.validation.recipientRequired`))
      return
    }

    const replyToValue = replyTo.trim()
    if (!replyToValue) {
      setReplyToError(t(`${basePath}.validation.replyToRequired`))
      return
    }
    if (!emailSchema.safeParse(replyToValue).success) {
      setReplyToError(t(`${basePath}.validation.invalidEmail`))
      return
    }

    // Submit every selectable product: ready ones send now, generatable ones
    // get a proof job enqueued server-side and the email is held until all
    // proofs complete.
    const selectableIds = new Set(
      products
        .filter((product) => product.ready || product.canGenerate)
        .map((p) => p.orderProductId),
    )
    const orderProductIds = Array.from(selectedProductIds).filter((id) =>
      selectableIds.has(id),
    )

    if (orderProductIds.length === 0) {
      setToast({
        text: t(`${basePath}.validation.productRequired`),
        icon: 'Error',
      })
      return
    }

    setIsSubmitting(true)
    try {
      const response = await createProofShare({
        orderId,
        applicationType,
        orderProductIds,
        recipientEmails: finalRecipients,
        replyToEmail: replyToValue,
        comment: comment.trim() || undefined,
      })
      setToast({
        text: response.data.pending
          ? t(`${basePath}.toasts.pendingSuccess`)
          : t(`${basePath}.toasts.success`),
        icon: 'Check',
      })
      onClose()
    } catch (error) {
      console.error('Failed to share proof:', error)
      // Surface the backend's message when it sent one (mirrors the M3 modal);
      // fall back to the generic copy.
      const serverMessage = (
        error as { response?: { data?: { message?: string } } }
      )?.response?.data?.message
      setToast({
        text: serverMessage || t(`${basePath}.toasts.error`),
        icon: 'Error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <FullscreenModal show={[show, onClose]} hideClose={false}>
      <Flex
        column
        gap="24px"
        padding="32px 32px 40px"
        width="600px"
        maxHeight="90vh"
        overflow="auto"
        data-testid="share-proof-modal"
      >
        <Flex column gap="8px">
          <Text size="24px" weight="600">
            {t(`${basePath}.title`)}
          </Text>
          <Text color="gray2">{t(`${basePath}.intro`)}</Text>
        </Flex>

        {/* Reply-To — prefilled with the current user's email, editable */}
        <Flex column gap="4px">
          <Input
            type="email"
            label={t(`${basePath}.replyToLabel`)}
            value={[
              replyTo,
              (value: string) => {
                setReplyTo(value)
                if (replyToError) setReplyToError(null)
              },
            ]}
            placeholder={t(`${basePath}.recipientPlaceholder`)}
            error={replyToError ?? undefined}
            data-testid="share-proof-reply-to-input"
          />
          <Text size="12px" color="gray2">
            {t(`${basePath}.replyToHelper`)}
          </Text>
        </Flex>

        {/* Recipients */}
        <Flex column gap="8px">
          {recipients.length > 0 && (
            <S.ChipsWrapper data-testid="share-proof-chips">
              {recipients.map((email) => (
                <S.Chip key={email}>
                  <Text size="14px">{email}</Text>
                  <S.ChipRemoveButton
                    type="button"
                    onClick={() => removeEmail(email)}
                    aria-label={t(`${basePath}.removeRecipient`, { email })}
                  >
                    <Icon icon="exitModal" width="12px" fill="gray2" />
                  </S.ChipRemoveButton>
                </S.Chip>
              ))}
            </S.ChipsWrapper>
          )}
          <Flex gap="8px" align="flex-start">
            <Input
              type="email"
              label={t(`${basePath}.recipientsLabel`)}
              value={[
                emailInput,
                (value: string) => {
                  setEmailInput(value)
                  if (emailError) setEmailError(null)
                },
              ]}
              onEnter={() => addEmail(emailInput)}
              placeholder={t(`${basePath}.recipientPlaceholder`)}
              error={emailError ?? undefined}
              data-testid="share-proof-recipient-input"
            />
            {/* `default`, not `outline`: outline locks white text/fill for the
                dark editor header and disappears on the light modal bg. */}
            <Button
              default
              margin="20px 0 0 0"
              iconLeft="plus"
              onClick={() => addEmail(emailInput)}
              disabled={!emailInput.trim()}
              data-testid="share-proof-add-recipient"
            >
              {t(`${basePath}.addRecipient`)}
            </Button>
          </Flex>
          <Text size="12px" color="gray2">
            {t(`${basePath}.recipientsHelper`, {
              max: MAX_PROOF_SHARE_RECIPIENTS,
            })}
          </Text>
        </Flex>

        {/* Additional comments to family — optional free-text director note */}
        <Flex column gap="4px">
          <TextArea
            label={t(`${basePath}.commentLabel`)}
            aria-label={t(`${basePath}.commentLabel`)}
            value={[comment, setComment]}
            placeholder={t(`${basePath}.commentPlaceholder`)}
            maxLength={MAX_PROOF_SHARE_COMMENT_LENGTH}
            data-testid="share-proof-comment-input"
          />
        </Flex>

        {/* Products */}
        <Flex column gap="8px">
          <Text size="12px" weight="500">
            {t(`${basePath}.productsLabel`)}
          </Text>
          {isLoadingProducts ? (
            <Text color="gray2">{t(`${basePath}.loadingProducts`)}</Text>
          ) : products.length === 0 ? (
            <Text color="gray2">{t(`${basePath}.noProducts`)}</Text>
          ) : (
            <S.ProductList>
              {products.map((product) => {
                const selectable = product.ready || Boolean(product.canGenerate)
                return (
                  <S.ProductRow
                    key={product.orderProductId}
                    disabled={!selectable}
                    data-testid="share-proof-product"
                  >
                    <S.ProductCheckbox
                      type="checkbox"
                      checked={
                        selectable &&
                        selectedProductIds.has(product.orderProductId)
                      }
                      disabled={!selectable}
                      onChange={() => toggleProduct(product.orderProductId)}
                      aria-label={product.productName}
                    />
                    <Text>{product.productName}</Text>
                    {!product.ready && (
                      <>
                        <S.StatusBadge>
                          {selectable
                            ? t(`${basePath}.status.willGenerate`)
                            : t(`${basePath}.status.unavailable`)}
                        </S.StatusBadge>
                        <Tooltip
                          text={
                            selectable
                              ? t(`${basePath}.status.willGenerateTooltip`)
                              : t(`${basePath}.status.noProofTooltip`)
                          }
                          width="220px"
                        >
                          <Icon icon="info" width="16px" fill="gray2" />
                        </Tooltip>
                      </>
                    )}
                  </S.ProductRow>
                )
              })}
            </S.ProductList>
          )}
        </Flex>

        {/* Actions */}
        <Flex gap="12px" justify="flex-end">
          <Button
            default
            onClick={onClose}
            disabled={isSubmitting}
            data-testid="share-proof-cancel"
          >
            {t(`${basePath}.cancel`)}
          </Button>
          <Button
            primary
            onClick={() => void handleSubmit()}
            disabled={isSubmitting || isLoadingProducts}
            data-testid="share-proof-submit"
          >
            {isSubmitting
              ? t(`${basePath}.submitting`)
              : t(`${basePath}.submit`)}
          </Button>
        </Flex>
      </Flex>
    </FullscreenModal>
  )
}

export default ShareProofModal
